import React, { FunctionComponent, useEffect, useState } from 'react';
import {
    Col,
    Dropdown,
    DropdownButton,
    Row,
} from 'react-bootstrap';
import { ApplicationState } from '../../../redux/States';
import { ErrorDispatches } from '../../../redux/Dispatches';
import { ConnectedProps, connect } from 'react-redux';
import {
    AuthedLayout,
    EditableTableDiv,
    HeaderDisplayKeysWithType,
} from '../../../components';
import { useInjuryDate, useSemGrade } from '../../../hook';
import {
    InjuryMomentEnum,
    InjuryPlaceEnum,
    StudentGradeNoSeat,
    StudentInjury,
    UserInfo,
} from '../../../model';
import apis from '../../../apis';
import {
    downloadDataAsExcel,
    downloadDataAsPDFByPrint,
    serializeInjury,
    SheetHeaderDisplay,
} from '../../../utils';
import { DateTime } from 'luxon';
import { I18N } from '../../../i18n-raw';
import { toast } from 'react-toastify';

type PageData =
    StudentInjury &
    StudentGradeNoSeat;

const tableHeader: (
    SheetHeaderDisplay<PageData> &
    HeaderDisplayKeysWithType<PageData>
)[] = [
        {
            display: '年',
            property: 'grade',
        },
        {
            display: '班級',
            property: 'className',
            onRender: (value: unknown) => {
                const num =
                    parseInt(
                        value as string,
                        10
                    );

                return !isNaN(num) &&
                    num > 100 &&
                    num < 200
                    ? num - 100
                    : value;
            },
            onSheetRender: (value) => {
                const num =
                    parseInt(
                        value as string,
                        10
                    );

                if (!isNaN(num)) {
                    return num > 100 &&
                        num < 200
                        ? num - 100
                        : num;
                }

                return (
                    value?.toString() ??
                    ''
                );
            },
        },
        {
            display: '座號',
            property: 'seat',
        },
        {
            display: '姓名',
            property: 'name',
        },
        {
            display: '性別',
            property: 'sex',
            onRender: (v) =>
                v === '1'
                    ? '男'
                    : '女',
            onSheetRender: (v) =>
                v === '1'
                    ? '男'
                    : '女',
            style: {
                width: '7%',
            },
        },
        {
            display: '時段',
            property: 'momentEnum',
            onRender: (v) => {
                return typeof v ===
                    'number'
                    ? I18N.InjuryMoment[
                    v as InjuryMomentEnum
                    ]
                    : '';
            },
            onSheetRender: (v) => {
                return typeof v ===
                    'number'
                    ? I18N.InjuryMoment[
                    v as InjuryMomentEnum
                    ]
                    : '';
            },
        },
        {
            display: '地點',
            property: 'placeEnum',
            onRender: (v) => {
                return typeof v ===
                    'number'
                    ? I18N.InjuryPlace[
                    v as InjuryPlaceEnum
                    ]
                    : '';
            },
            onSheetRender: (v) => {
                return typeof v ===
                    'number'
                    ? I18N.InjuryPlace[
                    v as InjuryPlaceEnum
                    ]
                    : '';
            },
        },
        {
            display: '日期時間',
            property: 'date',
            onRender: (v) => {
                return v instanceof DateTime
                    ? v.toFormat(
                        'yyyy/MM/dd HH:mm'
                    )
                    : null;
            },
            onSheetRender: (v) => {
                return v instanceof DateTime
                    ? v.toFormat(
                        'yyyy/MM/dd HH:mm'
                    )
                    : '';
            },
        },
        {
            display: '情形',
            property: 'memos',
            onRender: (
                v,
                i,
                e,
                c
            ) => {
                const {
                    allValues,
                } = c;

                return serializeInjury(
                    allValues
                );
            },
            onSheetRender: (
                value,
                idx,
                {
                    context,
                    allValues,
                }
            ) => {
                return serializeInjury(
                    allValues
                );
            },
        },
    ];

const mapState = (
    app: ApplicationState
) => ({
    ...app.auth,
});

const mapDispatch =
    ErrorDispatches;

const connector = connect(
    mapState,
    mapDispatch
);

type Props =
    ConnectedProps<typeof connector>;

const injuryAccListSortByClass:
    FunctionComponent<Props> = ({
        user,
        catchErrorForModal,
    }) => {
        const {
            yearSem,
            element:
            semGradeElement,
        } = useSemGrade();

        const {
            selectedInjuryDate,
            element:
            injuryDateelement,
        } = useInjuryDate({
            yearSem,
        });

        const [
            students,
            setStudents,
        ] =
            useState<PageData[]>(
                []
            );

        const [
            userinfo,
            setUser,
        ] =
            useState<UserInfo>();

        useEffect(() => {
            if (
                yearSem &&
                selectedInjuryDate
            ) {
                apis
                    .getUserInfo()
                    .then((u) => {
                        setUser(u);
                    })
                    .catch(
                        catchErrorForModal
                    );

                toast
                    .promise(
                        apis
                            .getInjuryAccListSortByClass(
                                yearSem.year,
                                yearSem.sem,
                                selectedInjuryDate.date
                            )
                            .then(
                                (r) => {
                                    setStudents(
                                        r.map(
                                            ({
                                                studentInjury,
                                                ...s
                                            }) => ({
                                                ...studentInjury,
                                                ...s,
                                            })
                                        )
                                    );
                                }
                            ),
                        {
                            pending:
                                '資料載入中......',
                            success:
                                '資料載入成功！',
                            error:
                                '資料載入失敗！請查看錯誤資訊。',
                        }
                    )
                    .catch(
                        catchErrorForModal
                    );
            }
        }, [
            selectedInjuryDate,
            yearSem?.year,
            yearSem?.sem,
        ]);

        const title =
            `${user.schools[user.currentSchool].name}` +
            `${yearSem?.year ?? ''}學年` +
            `${yearSem?.sem ?? ''}學期_傷病日誌(分日期依班級排序)`;

        const getAllStudents =
            async (): Promise<PageData[]> => {
                if (!yearSem) {
                    return [];
                }

                const r =
                    await apis
                        .getInjuryAccListSortByClass(
                            yearSem.year,
                            yearSem.sem
                        );

                return r
                    .map(
                        ({
                            studentInjury,
                            ...s
                        }) => ({
                            ...studentInjury,
                            ...s,
                        })
                    )
                    .sort(
                        (
                            a,
                            b
                        ) => {
                            if (
                                !a.date &&
                                !b.date
                            ) {
                                return 0;
                            }

                            if (
                                !a.date
                            ) {
                                return 1;
                            }

                            if (
                                !b.date
                            ) {
                                return -1;
                            }

                            const da =
                                new Date(
                                    a.date.year,
                                    a.date.month -
                                    1,
                                    a.date.day
                                ).getTime();

                            const db =
                                new Date(
                                    b.date.year,
                                    b.date.month -
                                    1,
                                    b.date.day
                                ).getTime();

                            return db - da;
                        }
                    );
            };

        return (
            <AuthedLayout>
                <Row className="justify-content-between">
                    <Col>
                        <Row>
                            <Col
                                xs={3}
                                className="mr-3"
                            >
                                {
                                    semGradeElement
                                }
                            </Col>

                            <Col
                                xs={3}
                                className="mr-3"
                            >
                                {
                                    injuryDateelement
                                }
                            </Col>
                        </Row>
                    </Col>

                    <div className="d-flex">
                        <DropdownButton
                            id="injury-class-all-export"
                            title="所有日期"
                            variant="info"
                            className="mr-3"
                            disabled={!yearSem}
                        >
                            <Dropdown.Item
                                onClick={async () => {
                                    if (
                                        !yearSem
                                    ) {
                                        return;
                                    }

                                    try {
                                        const allStudents =
                                            await getAllStudents();

                                        downloadDataAsExcel({
                                            title,
                                            values:
                                                allStudents,
                                            headers:
                                                tableHeader,
                                            groupBy:
                                                (
                                                    v
                                                ) =>
                                                    `${v.date?.year}-${v.date?.month}-${v.date?.day}`,
                                            footer:
                                                '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                            exportfile:
                                                userinfo
                                                    ?.exportFileType,
                                        });
                                    } catch (
                                    error
                                    ) {
                                        catchErrorForModal(
                                            error as any
                                        );
                                    }
                                }}
                            >
                                Excel 下載
                            </Dropdown.Item>

                            <Dropdown.Item
                                onClick={async () => {
                                    if (
                                        !yearSem
                                    ) {
                                        return;
                                    }

                                    try {
                                        const allStudents =
                                            await getAllStudents();

                                        downloadDataAsPDFByPrint({
                                            title,
                                            values:
                                                allStudents,
                                            headers:
                                                tableHeader,
                                            groupBy:
                                                (
                                                    v
                                                ) =>
                                                    `${v.date?.year}-${v.date?.month}-${v.date?.day}`,
                                            footer:
                                                '承辦人:　　　　　　　　　　　　　組長:　　　　　　　　　　　　　主任:　　　　　　　　　　　　　　校長:　　　　　　　　　　　　',
                                        });
                                    } catch (
                                    error
                                    ) {
                                        catchErrorForModal(
                                            error as any
                                        );
                                    }
                                }}
                            >
                                列印
                            </Dropdown.Item>
                        </DropdownButton>

                        <DropdownButton
                            id="injury-class-single-export"
                            title="單一日期"
                            variant="success"
                            disabled={
                                !students.length
                            }
                        >
                            <Dropdown.Item
                                onClick={() => {
                                    downloadDataAsExcel({
                                        title,
                                        values:
                                            students,
                                        headers:
                                            tableHeader,
                                        groupBy:
                                            () =>
                                                `${selectedInjuryDate?.date ?? ''}`,
                                        footer:
                                            '承辦人:          組長:         主任:         校長:',
                                        exportfile:
                                            userinfo
                                                ?.exportFileType,
                                    });
                                }}
                            >
                                Excel 下載
                            </Dropdown.Item>

                            <Dropdown.Item
                                onClick={() => {
                                    downloadDataAsPDFByPrint({
                                        title,
                                        values:
                                            students,
                                        headers:
                                            tableHeader,
                                        groupBy:
                                            () =>
                                                `${selectedInjuryDate?.date ?? ''}`,
                                        footer:
                                            '承辦人:          組長:         主任:         校長:',
                                    });
                                }}
                            >
                                列印
                            </Dropdown.Item>
                        </DropdownButton>
                    </div>
                </Row>

                <hr />

                <Row>
                    <EditableTableDiv
                        headers={
                            tableHeader
                        }
                        values={
                            students
                        }
                    />
                </Row>
            </AuthedLayout>
        );
    };

export const InjuryAccListSortByClass =
    connector(
        injuryAccListSortByClass
    );